import { useEffect, useState } from 'react';
import { api } from '../api';
import ExportButton from '../components/ExportButton';

export default function Inventory() {
  const [stock, setStock] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [tab, setTab] = useState<'stock' | 'in' | 'out' | 'all'>('stock');
  const [showIn, setShowIn] = useState(false);
  const [showOut, setShowOut] = useState(false);
  const [prods, setProds] = useState<any[]>([]);
  const [deps, setDeps] = useState<any[]>([]);
  const [form, setForm] = useState({ product_id: '', quantity: '', unit_cost: '', department_id: '' });

  const loadStock = () => api('/inventory/stock').then(setStock).catch(() => {});
  const loadRecords = (type?: string) => api('/inventory/records' + (type ? '?type=' + type : '')).then(setRecords).catch(() => {});

  useEffect(() => {
    loadStock();
    api('/products').then(setProds).catch(() => {});
    api('/departments').then(setDeps).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 'stock') loadRecords(tab === 'all' ? undefined : tab);
  }, [tab]);

  // 入库
  const doIn = async () => {
    if (!form.product_id || !form.quantity) return alert('请填写完整');
    await api('/inventory/in', {
      method: 'POST',
      body: JSON.stringify({
        product_id: Number(form.product_id),
        quantity: Number(form.quantity),
        unit_cost: Number(form.unit_cost) || 0,
        department_id: Number(form.department_id) || null,
      }),
    });
    setShowIn(false); setForm({ product_id: '', quantity: '', unit_cost: '', department_id: '' }); loadStock();
  };

  // 出库
  const doOut = async () => {
    if (!form.product_id || !form.quantity) return alert('请填写完整');
    try {
      await api('/inventory/out', {
        method: 'POST',
        body: JSON.stringify({
          product_id: Number(form.product_id),
          quantity: Number(form.quantity),
          department_id: Number(form.department_id) || null,
        }),
      });
      setShowOut(false); setForm({ product_id: '', quantity: '', unit_cost: '', department_id: '' }); loadStock();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const totalValue = stock.reduce((s, p) => s + (p.current || 0) * (p.cost || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">进出库存管理</h2>
          <p className="text-sm text-slate-500 mt-1">入库 → 库存 → 出库 全流程追踪</p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            filename="当前库存"
            fetchData={() => api('/inventory/stock')}
            columns={[
              { header: '编码', key: 'code' },
              { header: '名称', key: 'name' },
              { header: '分类', key: 'category' },
              { header: '单位', key: 'unit' },
              { header: '累计入库', key: 'stock_in' },
              { header: '累计出库', key: 'stock_out' },
              { header: '当前库存', key: 'current' },
              { header: '成本单价', key: 'cost' },
              { header: '库存总值', key: 'current', format: (r) => (Number(r.current || 0) * Number(r.cost || 0)).toFixed(2) },
            ]}
          />
          <button onClick={() => { setForm({ product_id: '', quantity: '', unit_cost: '', department_id: '' }); setShowIn(true); }} className="bg-green-600 text-white rounded-lg px-4 py-2 hover:bg-green-700 text-sm">
            + 入库
          </button>
          <button onClick={() => { setForm({ product_id: '', quantity: '', unit_cost: '', department_id: '' }); setShowOut(true); }} className="bg-orange-600 text-white rounded-lg px-4 py-2 hover:bg-orange-700 text-sm">
            − 出库
          </button>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { key: 'stock' as const, label: '当前库存' },
          { key: 'all' as const, label: '全部流水' },
          { key: 'in' as const, label: '入库记录' },
          { key: 'out' as const, label: '出库记录' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-1.5 rounded-md text-sm transition ${tab === t.key ? 'bg-white shadow font-medium' : 'hover:bg-slate-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 当前库存视图 */}
      {tab === 'stock' && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-xs text-slate-500">SKU 数量</div>
              <div className="text-2xl font-bold mt-1">{stock.length}</div>
            </div>
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-xs text-slate-500">库存总量</div>
              <div className="text-2xl font-bold mt-1 text-blue-600">{stock.reduce((s, p) => s + (p.current || 0), 0)}</div>
            </div>
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-xs text-slate-500">库存总值(成本)</div>
              <div className="text-2xl font-bold mt-1 text-green-600">¥{totalValue.toLocaleString()}</div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="p-3">编码</th><th>产品名</th><th>分类</th><th>单位</th><th>入库量</th><th>出库量</th><th>当前库存</th><th>成本价</th><th>库存价值</th>
                </tr>
              </thead>
              <tbody>
                {stock.map((p) => {
                  const cur = p.current || 0;
                  const val = cur * (p.cost || 0);
                  return (
                    <tr key={p.id} className={`border-t hover:bg-slate-50 ${cur <= 0 ? 'bg-red-50' : cur < 10 ? 'bg-yellow-50' : ''}`}>
                      <td className="p-3 font-mono text-xs">{p.code || '—'}</td>
                      <td className="p-3 font-medium">{p.name}</td>
                      <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs">{p.category || '—'}</span></td>
                      <td className="p-3">{p.unit || '—'}</td>
                      <td className="p-3 text-green-600">{p.stock_in || 0}</td>
                      <td className="p-3 text-red-500">{p.stock_out || 0}</td>
                      <td className="p-3 font-bold">{cur}</td>
                      <td className="p-3">¥{Number(p.cost || 0).toLocaleString()}</td>
                      <td className="p-3 text-slate-600">¥{val.toLocaleString()}</td>
                    </tr>
                  );
                })}
                {stock.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">暂无库存数据</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* 流水记录 */}
      {(tab === 'all' || tab === 'in' || tab === 'out') && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left">
                <th className="p-3">类型</th><th>产品</th><th>数量</th><th>单价</th><th>总价</th><th>部门</th><th>时间</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className={`border-t hover:bg-slate-50 ${r.type === 'in' ? 'bg-green-50/30' : 'bg-orange-50/30'}`}>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${r.type === 'in' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {r.type === 'in' ? '入库' : '出库'}
                    </span>
                  </td>
                  <td className="p-3 font-medium">{r.product_name}</td>
                  <td className="p-3 font-medium">{r.quantity}</td>
                  <td className="p-3">{r.unit_cost ? `¥${Number(r.unit_cost).toLocaleString()}` : '—'}</td>
                  <td className="p-3">{r.total_cost ? `¥${Number(r.total_cost).toLocaleString()}` : '—'}</td>
                  <td className="p-3 text-xs"><span className="px-2 py-0.5 bg-slate-100 rounded">{r.department_name || '—'}</span></td>
                  <td className="p-3 text-xs text-slate-500">{(r.created_at || '').replace('T', ' ')}</td>
                </tr>
              ))}
              {records.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">暂无记录</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* 入库弹窗 */}
      {showIn && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowIn(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4 text-green-700">入库登记</h3>
            <div className="space-y-3">
              <select className="w-full border rounded-lg px-3 py-2" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                <option value="">选择产品</option>
                {prods.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
              </select>
              <input className="w-full border rounded-lg px-3 py-2" type="number" placeholder="入库数量 *" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2" type="number" placeholder="单位成本" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
              <select className="w-full border rounded-lg px-3 py-2" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">选择部门</option>
                {deps.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowIn(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={doIn} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">确认入库</button>
            </div>
          </div>
        </div>
      )}

      {/* 出库弹窗 */}
      {showOut && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowOut(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4 text-orange-700">出库登记</h3>
            <div className="space-y-3">
              <select className="w-full border rounded-lg px-3 py-2" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                <option value="">选择产品</option>
                {prods.map((p) => {
                  const item = stock.find((s) => s.id === p.id);
                  return <option key={p.id} value={p.id}>{p.name} (库存: {item?.current || 0})</option>;
                })}
              </select>
              <input className="w-full border rounded-lg px-3 py-2" type="number" placeholder="出库数量 *" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              <select className="w-full border rounded-lg px-3 py-2" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">选择部门</option>
                {deps.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowOut(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={doOut} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">确认出库</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
